import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Tooltip,
  useTheme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import useCallAgentPermissionFlags from '@src/hooks/call/useCallAgentPermissionFlags';
import {
  GetContactsRequest,
  GetContactsResponseData,
  useLazyCheckWhatsAppAccountQuery,
  useLazyGetAvailableMessagingAccountsQuery,
  useLazyGetContactsQuery,
} from '@src/reducers/contactsApi';
import CallStartIcon from '@src/resources/icons/CallStartIcon';
import { ConversationStartIcon } from '@src/resources/icons/ConversationStartIcon';
import { reachedEndOfList } from '@src/utils/scrollUtils';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import IconWrapper from '../Common/IconWrapper';

const THRESHOLD_10 = 10;

const getContactsRequest: GetContactsRequest = {
  filter: {
    createdAtFrom: '',
    createdAtTo: '',
    searchFilter: '',
    onlyBlacklisted: false,
    searchByLastUpdate: false,
    companyFilter: '',
    tagFilter: [],
    assignTo: null,
  },
  sorting: [],
  limit: 10,
  offset: 1,
};

const fromDate = dayjs()
  .subtract(7, 'day')
  .hour(0)
  .minute(0)
  .second(0)
  .millisecond(0)
  .toISOString();

const toDate = dayjs()
  .hour(23)
  .minute(59)
  .second(59)
  .millisecond(999)
  .toISOString();

const ContactList: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
  setOpenChatWithContact: (contact: GetContactsResponseData) => void;
  setOpenChatWithCall: (contact: GetContactsResponseData) => void;
}> = ({ open, setOpen, setOpenChatWithContact, setOpenChatWithCall }) => {
  const { formatMessage } = useIntl();
  const { isVisible, isEnabled } = useCallAgentPermissionFlags();
  const theme = useTheme();
  const classes = useStyles();

  const [searchPattern, setSearchPattern] = useState('');
  const [debouncedSearchPattern, setDebouncedSearchPattern] = useState('');

  const [offset, setOffset] = useState(1);
  const [contactsArray, setContactsArray] = useState<GetContactsResponseData[]>(
    []
  );

  const [hasNextPage, setHasNextPage] = useState(false);

  const scrollRef = useRef<HTMLUListElement>(null);

  const [triggerGetContacts, { data: contacts, isLoading }] =
    useLazyGetContactsQuery({});

  const [triggerCheckWhatsAppAccount] = useLazyCheckWhatsAppAccountQuery();

  const [triggerGetAvailableMessagingAccounts] =
    useLazyGetAvailableMessagingAccountsQuery();

  const handleStartCall = (contact: GetContactsResponseData) => {
    triggerCheckWhatsAppAccount({ contactId: `${contact.id}` });
    triggerGetAvailableMessagingAccounts({ contactId: `${contact.id}` });
    setOpenChatWithCall(contact);

    setOpen(false);
  };

  const handleStartConversation = (contact: GetContactsResponseData) => {
    triggerCheckWhatsAppAccount({ contactId: `${contact.id}` });
    triggerGetAvailableMessagingAccounts({ contactId: `${contact.id}` });
    setOpenChatWithContact(contact);

    setOpen(false);
  };

  const makeRequest = () => {
    triggerGetContacts(
      {
        ...getContactsRequest,
        filter: {
          ...getContactsRequest.filter,
          searchFilter: searchPattern,
          createdAtFrom: searchPattern ? '' : fromDate,
          createdAtTo: searchPattern ? '' : toDate,
        },
        offset,
      },
      true
    );
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchPattern(searchPattern);
    }, 500);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchPattern]);

  useEffect(() => {
    setContactsArray([]);
    setOffset(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchPattern]);

  useEffect(() => {
    setContactsArray(prevContacts => {
      const newContacts = contacts?.data || [];
      return [...prevContacts, ...newContacts];
    });

    setHasNextPage(contacts?.paging.hasMoreItems || false);
  }, [contacts]);

  // Note: handle when pattern changes
  useEffect(() => {
    if (offset === 1) makeRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchPattern]);

  // Note: handle next page
  useEffect(() => {
    makeRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      classes={{
        paper: classes.paper,
      }}
    >
      <DialogContent
        style={{
          minWidth: '600px',
          borderRadius: '8px',
          height: '400px',
          padding: 0,
        }}
      >
        <Box style={{ height: '300px' }}>
          <Box p="16px 16px 0 16px">
            <Box className={classes.searchContainer}>
              <SearchIcon className={classes.searchIcon} />
              <InputBase
                className={classes.input}
                inputProps={{ 'data-testid': 'contact-search-input' }}
                placeholder={formatMessage({
                  id: 'Contact.console.contactList.search.placeholder',
                })}
                value={searchPattern}
                onChange={e => setSearchPattern(e.target.value)}
              />
            </Box>
          </Box>

          <List
            className={classes.list}
            ref={scrollRef}
            onScroll={() => {
              if (reachedEndOfList(scrollRef, THRESHOLD_10) && hasNextPage) {
                setOffset(prevOffset => prevOffset + 1);
                setHasNextPage(false);
              }
            }}
          >
            {contactsArray?.map((contact, ind) => {
              const { id, fullName, mobileNumber, email } = contact;
              return (
                <ListItem
                  key={id}
                  data-testid="contact-row"
                  data-contact-id={id}
                  className={classes.listItem}
                  style={{
                    backgroundColor:
                      ind % 2 === 0
                        ? theme.palette.action.hover
                        : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={fullName || ''}
                    classes={{
                      primary: classes.listItemText,
                    }}
                  />
                  <ListItemText
                    primary={mobileNumber || ''}
                    classes={{
                      primary: classes.listItemText,
                    }}
                  />
                  <ListItemText
                    primary={email || ''}
                    classes={{
                      primary: classes.listItemText,
                    }}
                  />

                  <Box className={classes.actions}>
                    {isVisible && (
                      <IconButton
                        className={classes.phoneButton}
                        onClick={() => handleStartCall(contact)}
                        size="small"
                        disabled={!isEnabled}
                      >
                        <IconWrapper fontSize={22}>
                          <CallStartIcon />
                        </IconWrapper>
                      </IconButton>
                    )}
                    <Tooltip
                      title={`${formatMessage({
                        id: 'Contact.Grid.ButtonLabel.StartChat',
                      })} ${fullName}`}
                    >
                      <IconButton
                        className={classes.messageButton}
                        data-testid="start-conversation-button"
                        onClick={() => handleStartConversation(contact)}
                        size="small"
                      >
                        <IconWrapper fontSize={20}>
                          <ConversationStartIcon />
                        </IconWrapper>
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              );
            })}
            {contactsArray.length === 0 && !isLoading && (
              <ListItem className={classes.listItem}>
                <ListItemText>
                  {formatMessage({
                    id: 'Contact.console.contactList.search.noResults',
                  })}
                </ListItemText>{' '}
              </ListItem>
            )}
          </List>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  paper: {
    minWidth: '610px',
    borderRadius: '8px',
    border: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
  },
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor:
      theme.palette.type === 'dark' ? theme.palette.grey[800] : '#f9fafb',
    borderRadius: 50,
    padding: '8px 16px',
    marginBottom: theme.spacing(2),
    border: `1px solid ${
      theme.palette.type === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb'
    }`,
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: theme.palette.text.primary,
    '& .MuiInputBase-input': {
      padding: 0,
    },
    height: 10,
  },
  list: {
    padding: 0,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'start',
    flexDirection: 'column',
    overflowY: 'auto',
    height: '316px',
  },
  listItem: {
    padding: '9px 16px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  listItemText: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    fontWeight: 400,
    width: 170,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  phoneButton: {
    color: '#10b981',
  },
  messageButton: {
    color: '#6366f1',
  },
}));

export default ContactList;
